import { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Country, PaymentType } from '@afterpay/types'
import { Route } from '../../config/router'
import { CheckoutStatus } from '../../model/checkout'
import { isCountryNeedsBiometricVerification, isCountryUsePersonaFlow } from '../../model/country'
import {
  useCheckoutError,
  useCheckoutState,
  useCheckoutStatus,
  useCheckoutToken,
  useCohort,
  useCountry,
  useIsInlinePaymentOptions,
  useSelectedPaymentType,
} from '../../state'
import { isAppleRetailOrder } from '../../state/checkout/helper'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import { getCheckoutJourneyTags } from './checkoutTags'
import {
  IDENTITY_VERIFICATION_SIGNALS,
  USER_JOURNEY_NAMES,
  USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS,
  USER_JOURNEY_TIMEOUT_MS,
  USER_JOURNEY_VARIANTS,
} from './constants'
import { clearJourneyMarker, isMarkerFromCurrentPageLoad, readJourneyMarker } from './journeyMarker'
import { useJourneyNetworkSignals } from './networkSignals'
import { onBeforeRumSessionStop } from './sessionStopSignal'
import { mapTerminalErrorToJourney } from './terminalErrorMapping'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { getExpiredJourneyTimeoutTerminationReason, getReplacedJourneyCompletion } from './timeout'
import { useUserJourneyContext } from './UserJourneyContext'
import { useCheckoutJourneyOwnership } from './MobileUserJourneyOwnership'

const DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES = new Set<string>([
  '/apple-paylater',
  '/apple-paylater/in-store',
  '/apple-paylater/wallet-provisioning',
  '/apple-paylater/initialise',
])
const isCheckoutEntryRoute = (pathname: string): boolean =>
  pathname === Route.INDEX || pathname === '/[country]' || DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES.has(pathname)

const getTimeoutTerminationReason = (deadline: number): JourneyTerminationReason =>
  Date.now() - deadline > USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS || document.visibilityState === 'hidden'
    ? JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER
    : JourneyTerminationReason.TIMEOUT_WITH_ACTIVE_USER

const useTagActiveCheckoutJourney = (): (() => void) => {
  const { addTag, getActiveJourney } = useUserJourneyContext()
  const { initial, checkout } = useCheckoutState()
  const cohort = useCohort()
  const country = useCountry()

  return useCallback((): void => {
    const activeJourney = getActiveJourney()
    if (activeJourney?.name !== USER_JOURNEY_NAMES.CHECKOUT) return
    for (const [key, value] of getCheckoutJourneyTags({ merchantId: checkout?.merchant.id, country, cohort, existingTags: activeJourney.tags, initial })) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
  }, [addTag, checkout?.merchant.id, cohort, country, getActiveJourney, initial])
}

export const useStartCheckoutJourney = (suppressStart = false): void => {
  const { active, completeJourney, resumeJourney, startJourney } = useUserJourneyContext()
  const tagActiveCheckoutJourney = useTagActiveCheckoutJourney()
  const {
    canStartRocketshipJourney,
    claimRocketshipOwnership,
    didFallbackFromMobileNegotiation,
    owner,
    journeyId,
    startedAtEpochMs,
    reportingEnabled: enabled,
  } = useCheckoutJourneyOwnership()
  const checkoutToken = useCheckoutToken()
  const checkoutState = useCheckoutState()
  const checkoutStatus = useCheckoutStatus()
  const { pathname } = useRouter()
  const started = useRef(false)
  const observedCheckoutEntry = useRef(isCheckoutEntryRoute(pathname))
  const observedCheckoutInFlight = useRef(checkoutStatus !== CheckoutStatus.Confirmed && checkoutStatus !== CheckoutStatus.Closed)
  const observedCashReturn = useRef(pathname === Route.CASH_AUTH || pathname === Route.APPLE_PAYLATER_CASH_AUTH)
  const deferredEntryInitialData = useRef(DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES.has(pathname) ? checkoutState.initial : undefined)
  const awaitingDeferredCheckoutState = useRef(DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES.has(pathname))

  useEffect(() => {
    if (DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES.has(pathname) && !awaitingDeferredCheckoutState.current) {
      deferredEntryInitialData.current = checkoutState.initial
      awaitingDeferredCheckoutState.current = true
    }
    observedCheckoutEntry.current ||= isCheckoutEntryRoute(pathname)
    observedCheckoutInFlight.current ||= checkoutStatus !== CheckoutStatus.Confirmed && checkoutStatus !== CheckoutStatus.Closed
    const isCashReturnRoute = pathname === Route.CASH_AUTH || pathname === Route.APPLE_PAYLATER_CASH_AUTH
    observedCashReturn.current ||= isCashReturnRoute
    const marker = readJourneyMarker()
    const cashHandoffBelongsToCheckout =
      marker?.handoff === true &&
      (marker.checkoutToken === checkoutToken || (marker.checkoutToken === undefined && observedCashReturn.current)) &&
      marker.journey.name === USER_JOURNEY_NAMES.CHECKOUT
    const returnsFromCashGrant = isCashReturnRoute && cashHandoffBelongsToCheckout
    const checkoutJourneyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT
    if (returnsFromCashGrant) {
      return
    }
    if (checkoutJourneyActive) {
      started.current = true
      return
    }
    const hasCashGrantHandoffBoundary = cashHandoffBelongsToCheckout && !isCheckoutEntryRoute(pathname)
    const isTerminalRoute = pathname === Route.TERMINAL_ERROR || pathname === Route.APPLE_PAYLATER_OH_NO
    const terminalRouteWithoutJourneyBoundary =
      isTerminalRoute && !observedCheckoutEntry.current && !didFallbackFromMobileNegotiation && !hasCashGrantHandoffBoundary
    const hasMatchingPersistedBoundary =
      marker?.handoff !== true &&
      marker?.journey.name === USER_JOURNEY_NAMES.CHECKOUT &&
      !isMarkerFromCurrentPageLoad(marker) &&
      journeyId !== undefined &&
      marker.journey.id === journeyId
    const canResumeConfirmedNegotiation =
      checkoutStatus === CheckoutStatus.Confirmed &&
      ((didFallbackFromMobileNegotiation && (observedCheckoutInFlight.current || hasMatchingPersistedBoundary)) || hasCashGrantHandoffBoundary)
    const hasFreshDeferredCheckoutState =
      !awaitingDeferredCheckoutState.current || (checkoutState.initial !== deferredEntryInitialData.current && checkoutState.initial?.token === checkoutToken)
    const canStartOnCurrentRoute =
      !DEFERRED_APPLE_PAYLATER_ENTRY_ROUTES.has(pathname) &&
      hasFreshDeferredCheckoutState &&
      (observedCheckoutEntry.current ||
        (didFallbackFromMobileNegotiation && journeyId !== undefined) ||
        canResumeConfirmedNegotiation ||
        hasCashGrantHandoffBoundary)
    const canStartWithoutCheckoutToken = (didFallbackFromMobileNegotiation && journeyId !== undefined) || hasCashGrantHandoffBoundary
    const checkoutAlreadyEnded = checkoutStatus === CheckoutStatus.Closed || (checkoutStatus === CheckoutStatus.Confirmed && !canResumeConfirmedNegotiation)
    if (
      suppressStart ||
      !enabled ||
      (!checkoutToken && !canStartWithoutCheckoutToken) ||
      checkoutAlreadyEnded ||
      terminalRouteWithoutJourneyBoundary ||
      !canStartOnCurrentRoute ||
      owner !== 'rocketship' ||
      started.current ||
      !canStartRocketshipJourney()
    ) {
      return
    }
    if (didFallbackFromMobileNegotiation && claimRocketshipOwnership() === undefined) {
      return
    }
    started.current = true
    if (hasCashGrantHandoffBoundary) {
      resumeJourney(marker.journey, marker.esid)
      tagActiveCheckoutJourney()
      if (journeyId !== undefined && journeyId !== marker.journey.id) {
        const replacement = getReplacedJourneyCompletion(marker.journey.created)
        if (replacement.outcome === JourneyOutcome.TERMINATED) {
          completeJourney(USER_JOURNEY_NAMES.CHECKOUT, replacement.outcome, replacement.terminationReason)
        } else {
          completeJourney(USER_JOURNEY_NAMES.CHECKOUT, replacement.outcome)
        }
        startJourney(USER_JOURNEY_NAMES.CHECKOUT, startedAtEpochMs, journeyId)
        tagActiveCheckoutJourney()
        const timeoutTerminationReason = getExpiredJourneyTimeoutTerminationReason(startedAtEpochMs)
        if (timeoutTerminationReason !== undefined) {
          completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, timeoutTerminationReason)
        }
        return
      }
      const deadline = marker.journey.created + USER_JOURNEY_TIMEOUT_MS
      if (Date.now() >= deadline) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, getTimeoutTerminationReason(deadline))
      }
      return
    }
    if (hasMatchingPersistedBoundary) {
      resumeJourney(marker.journey, marker.esid)
      tagActiveCheckoutJourney()
      const deadline = marker.journey.created + USER_JOURNEY_TIMEOUT_MS
      if (Date.now() >= deadline) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, getTimeoutTerminationReason(deadline))
      }
      return
    }
    startJourney(USER_JOURNEY_NAMES.CHECKOUT, startedAtEpochMs, journeyId)
    tagActiveCheckoutJourney()
    const timeoutTerminationReason = getExpiredJourneyTimeoutTerminationReason(startedAtEpochMs)
    if (timeoutTerminationReason !== undefined) {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, timeoutTerminationReason)
    }
  }, [
    active?.name,
    canStartRocketshipJourney,
    claimRocketshipOwnership,
    checkoutStatus,
    checkoutState.initial,
    checkoutToken,
    completeJourney,
    didFallbackFromMobileNegotiation,
    enabled,
    journeyId,
    owner,
    pathname,
    resumeJourney,
    startJourney,
    startedAtEpochMs,
    suppressStart,
    tagActiveCheckoutJourney,
  ])
}

export const CheckoutJourneyStartAnchor: FunctionComponent<{ error?: Error | null }> = ({ error }) => {
  useStartCheckoutJourney(error != null)
  return null
}

const useClaimCheckoutJourneyForExit = (): (() => Promise<boolean>) => {
  const { completeJourney, getActiveJourney, resumeJourney, startJourney } = useUserJourneyContext()
  const { claimRocketshipOwnership, owner, reportingEligibilityResolved, waitForOwnershipResolution, waitForReportingEligibility } =
    useCheckoutJourneyOwnership()
  const tagActiveCheckoutJourney = useTagActiveCheckoutJourney()
  const checkoutToken = useCheckoutToken()

  return useCallback(async (): Promise<boolean> => {
    if (!reportingEligibilityResolved) {
      await waitForReportingEligibility()
    }
    if (getActiveJourney()?.name === USER_JOURNEY_NAMES.CHECKOUT) {
      tagActiveCheckoutJourney()
      return true
    }

    let boundary = claimRocketshipOwnership()
    if (owner === 'negotiating' && boundary === undefined) {
      await waitForOwnershipResolution()
      if (getActiveJourney()?.name === USER_JOURNEY_NAMES.CHECKOUT) {
        tagActiveCheckoutJourney()
        return true
      }
      boundary = claimRocketshipOwnership()
    }
    if (boundary === undefined) {
      return false
    }
    const marker = readJourneyMarker()
    const cashHandoffBelongsToCheckout = marker?.handoff !== true || marker.checkoutToken === checkoutToken
    const foreignCashHandoff = marker?.handoff === true && !cashHandoffBelongsToCheckout
    const resumesCurrentBoundary =
      marker?.journey.name === USER_JOURNEY_NAMES.CHECKOUT &&
      (marker.handoff === true
        ? cashHandoffBelongsToCheckout && (boundary.journeyId === undefined || marker.journey.id === boundary.journeyId)
        : boundary.journeyId !== undefined && marker.journey.id === boundary.journeyId)
    if (resumesCurrentBoundary) {
      resumeJourney(marker.journey, marker.esid)
    } else {
      if (marker !== undefined && !foreignCashHandoff) {
        resumeJourney(marker.journey, marker.esid)
        if (marker.handoff === true) {
          const replacement = getReplacedJourneyCompletion(marker.journey.created)
          if (replacement.outcome === JourneyOutcome.TERMINATED) {
            completeJourney(marker.journey.name, replacement.outcome, replacement.terminationReason)
          } else {
            completeJourney(marker.journey.name, replacement.outcome)
          }
        } else {
          completeJourney(marker.journey.name, isMarkerFromCurrentPageLoad(marker) ? JourneyOutcome.PROCESS_CRASHED : JourneyOutcome.PROCESS_KILLED)
        }
      } else if (foreignCashHandoff) {
        clearJourneyMarker()
      }
      startJourney(USER_JOURNEY_NAMES.CHECKOUT, boundary.startedAtEpochMs, boundary.journeyId)
    }
    tagActiveCheckoutJourney()
    return getActiveJourney()?.name === USER_JOURNEY_NAMES.CHECKOUT
  }, [
    claimRocketshipOwnership,
    checkoutToken,
    completeJourney,
    getActiveJourney,
    owner,
    reportingEligibilityResolved,
    resumeJourney,
    startJourney,
    tagActiveCheckoutJourney,
    waitForOwnershipResolution,
    waitForReportingEligibility,
  ])
}

export const useCompleteCheckoutJourneyBeforeSuccessfulExit = (): (() => Promise<void>) => {
  const { addTag, completeJourney, getActiveJourney, setVariant } = useUserJourneyContext()
  const claimCheckoutJourneyForExit = useClaimCheckoutJourneyForExit()
  const { checkout, started } = useCheckoutState()
  const paymentType = useSelectedPaymentType()

  return useCallback(async (): Promise<void> => {
    if (!(await claimCheckoutJourneyForExit())) {
      return
    }
    const activeJourney = getActiveJourney()
    if (activeJourney?.name !== USER_JOURNEY_NAMES.CHECKOUT) return
    if (!activeJourney.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${paymentType ?? PaymentType.PBI}`)
    }
    const planVariant = isAppleRetailOrder(checkout?.merchant.source)
      ? USER_JOURNEY_VARIANTS.APPLE_RETAIL
      : paymentType === PaymentType.PCL
      ? USER_JOURNEY_VARIANTS.PAY_MONTHLY
      : started?.paymentSchedules?.length === 2
      ? USER_JOURNEY_VARIANTS.PAY_IN_2
      : started?.paymentSchedules?.length === 4
      ? USER_JOURNEY_VARIANTS.PAY_IN_4
      : undefined
    if (planVariant !== undefined && activeJourney.variant !== planVariant) {
      setVariant(USER_JOURNEY_NAMES.CHECKOUT, planVariant)
    }
    const timeoutTerminationReason = getExpiredJourneyTimeoutTerminationReason(activeJourney.created)
    if (timeoutTerminationReason !== undefined) {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, timeoutTerminationReason)
    } else {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
    }
  }, [
    addTag,
    checkout?.merchant.source,
    claimCheckoutJourneyForExit,
    completeJourney,
    getActiveJourney,
    paymentType,
    setVariant,
    started?.paymentSchedules?.length,
  ])
}

export const useCompleteCheckoutJourneyOnTerminalError = (): (() => Promise<void>) => {
  const { active, addFriction, addFrustration, completeJourney, getActiveJourney } = useUserJourneyContext()
  const claimCheckoutJourneyForExit = useClaimCheckoutJourneyForExit()
  const checkoutError = useCheckoutError()

  const completeTerminalJourney = useCallback((): void => {
    const activeJourney = getActiveJourney()
    if (activeJourney?.name !== USER_JOURNEY_NAMES.CHECKOUT) {
      return
    }
    const timeoutTerminationReason = getExpiredJourneyTimeoutTerminationReason(activeJourney.created)
    if (timeoutTerminationReason !== undefined) {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, timeoutTerminationReason)
      return
    }
    const { outcome, signalType, signal } = mapTerminalErrorToJourney(checkoutError?.error, checkoutError?.errorCode)
    if (signalType === 'friction') {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
    } else {
      addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, outcome)
  }, [addFriction, addFrustration, checkoutError?.error, checkoutError?.errorCode, completeJourney, getActiveJourney])

  useEffect(() => completeTerminalJourney(), [active, completeTerminalJourney])

  return useCallback(async (): Promise<void> => {
    if (await claimCheckoutJourneyForExit()) {
      completeTerminalJourney()
    }
  }, [claimCheckoutJourneyForExit, completeTerminalJourney])
}

const idvTypeFromPathname = (pathname: string, country: Country | null | undefined): string | undefined => {
  if (pathname.includes(Route.VERIFY_RETRY)) {
    return 'profile-retry'
  }
  if (!pathname.includes(Route.VERIFY_IDENTITY)) {
    return undefined
  }
  if (pathname.includes(Route.VERIFY_ADDRESS)) {
    return 'profile-retry'
  }
  if (pathname.includes(Route.VERIFY_BIOMETRIC_PERSONA)) {
    return 'persona'
  }
  if (pathname.includes('/biometric') || pathname.includes(Route.VERIFY_IDENTITY_START)) {
    if (country == null) {
      return undefined
    }
    return isCountryUsePersonaFlow(country) ? 'persona' : 'biometric'
  }
  if (country == null) {
    return undefined
  }
  if (isCountryUsePersonaFlow(country)) {
    return 'persona'
  }
  return isCountryNeedsBiometricVerification(country) ? 'biometric' : 'document'
}

export const CheckoutJourneyAnchors: FunctionComponent = () => {
  const { active, addFriction, addTag, completeJourney, setVariant } = useUserJourneyContext()
  useJourneyNetworkSignals()
  const status = useCheckoutStatus()
  const cohort = useCohort()
  const country = useCountry()
  const paymentType = useSelectedPaymentType()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const { initial, checkout, started } = useCheckoutState()
  const { pathname } = useRouter()

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  const planVariant = isAppleRetailOrder(checkout?.merchant.source)
    ? USER_JOURNEY_VARIANTS.APPLE_RETAIL
    : paymentType === PaymentType.PCL
    ? USER_JOURNEY_VARIANTS.PAY_MONTHLY
    : started?.paymentSchedules?.length === 2
    ? USER_JOURNEY_VARIANTS.PAY_IN_2
    : started?.paymentSchedules?.length === 4
    ? USER_JOURNEY_VARIANTS.PAY_IN_4
    : undefined

  useEffect(() => {
    if (journeyActive && active !== undefined && planVariant !== undefined && active.variant !== planVariant) {
      setVariant(USER_JOURNEY_NAMES.CHECKOUT, planVariant)
    }
  }, [journeyActive, active, planVariant, setVariant])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    const tagOnce = (key: string, value: string | number | null | undefined): void => {
      if (value == null || value === '' || active.tags.some((tag) => tag.startsWith(`${key}:`))) {
        return
      }
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
    for (const [key, value] of getCheckoutJourneyTags({ merchantId: checkout?.merchant.id, country, cohort, existingTags: active.tags, initial })) {
      tagOnce(key, value)
    }
    tagOnce('idvType', idvTypeFromPathname(pathname, country))
  }, [journeyActive, active, addTag, checkout, cohort, country, initial, pathname])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.IdentityIneligible) {
      return
    }
    if (idvTypeFromPathname(pathname, country) === undefined) {
      return
    }
    if (!active.frictions.includes(IDENTITY_VERIFICATION_SIGNALS.REQUIRED)) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, IDENTITY_VERIFICATION_SIGNALS.REQUIRED)
    }
  }, [journeyActive, active, status, addFriction, pathname, country])

  useEffect(() => {
    if (!journeyActive || active === undefined || paymentType !== PaymentType.PCL) {
      return
    }
    if (isInlinePaymentOptions || !pathname.includes(Route.PAYMENT_OPTIONS)) {
      return
    }
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${PaymentType.PCL}`)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
  }, [journeyActive, active, paymentType, pathname, isInlinePaymentOptions, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.Confirmed) {
      return
    }
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${paymentType ?? PaymentType.PBI}`)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
  }, [journeyActive, active, status, paymentType, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive) {
      return undefined
    }
    const recordProcessKilled = (): void => {
      if (isCashGrantHandoffPending()) {
        return
      }
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.PROCESS_KILLED)
    }
    const unsubscribe = onBeforeRumSessionStop(recordProcessKilled)
    window.addEventListener('pagehide', recordProcessKilled)
    return () => {
      unsubscribe()
      window.removeEventListener('pagehide', recordProcessKilled)
    }
  }, [journeyActive, completeJourney])

  const activeJourneyStarted = active?.created
  useEffect(() => {
    if (!journeyActive || activeJourneyStarted === undefined) {
      return undefined
    }
    const deadline = activeJourneyStarted + USER_JOURNEY_TIMEOUT_MS
    const remaining = Math.max(0, deadline - Date.now())
    const timer = window.setTimeout(() => {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, getTimeoutTerminationReason(deadline))
    }, remaining)
    return () => {
      window.clearTimeout(timer)
    }
  }, [journeyActive, activeJourneyStarted, completeJourney])

  return null
}
